import type { Interrupt, ThreadTask } from '@langchain/langgraph-sdk';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PlayCircleIcon,
} from '@langchain/untitled-ui-icons';
import { CircularProgress } from '@mui/joy';

import { useContext, useState } from 'react';

import { CodeData } from '@/components/Code/Code';
import { Button, IconButton, Text } from '@/design-system';

import { ForeignDataTreeView } from '../src/components/debug/common/foreign-data-tree';
import { StateContext } from '../src/control/state';
import { foreignDataToTree, parseForeignData } from '../src/data/foreign-data';
import { useChatContext } from './hooks/useChatContext';

const InterruptFormContent = ({
  interrupt,
  task,
}: {
  interrupt: Interrupt;
  task: ThreadTask;
}) => {
  const { setRunState } = useContext(StateContext);
  const { submit, history, isLoading } = useChatContext();
  const [value, setValue] = useState('');

  const checkpoint = history.at(-1)?.checkpoint;

  const interruptId =
    interrupt.id ??
    // TODO: Initially we returned `interrupt_id`, remove later
    (interrupt as { interrupt_id?: string; id: string }).interrupt_id;

  const onSubmit = () => {
    if (!checkpoint) return;
    const resume = interruptId ? { [interruptId]: value } : value;

    setRunState({ type: 'running' });
    submit(undefined, { checkpoint, command: { resume } });
    setValue('');
  };

  return (
    <>
      <div className="rounded-md border border-brand-subtle bg-brand-tertiary p-3 text-sm">
        <span className="font-medium uppercase text-brand-primary">
          Interrupt
        </span>

        <div className="mt-1 text-primary">
          <ForeignDataTreeView
            data={foreignDataToTree({
              data: parseForeignData(interrupt.value),
            })}
          />
        </div>
      </div>
      <form
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.metaKey && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
      >
        <div className="rounded-md border border-secondary bg-secondary">
          <span className="text-md flex px-3 py-2 font-medium tracking-tighter">
            Provide a value to resume execution
          </span>
          <div className="h-px w-full border-t border-secondary" />
          <CodeData
            key={`${task.id}-${interruptId}-code-data`}
            value={value}
            onChange={setValue}
            language="json"
            actions={
              <div className="m-3 flex items-center gap-2">
                {isLoading ? (
                  <CircularProgress size="sm" />
                ) : (
                  <Button
                    onClick={onSubmit}
                    size="sm"
                    leftDecorator={PlayCircleIcon}
                  >
                    Resume
                  </Button>
                )}
              </div>
            }
          />
        </div>
      </form>
    </>
  );
};

export const InterruptForm = ({
  interrupts,
}: {
  interrupts: { interrupt: Interrupt; task: ThreadTask }[];
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const selectedItem = interrupts.at(selectedIndex);

  if (!selectedItem) return null;

  return (
    <div className="flex flex-col gap-2" key={`${selectedItem.task.id}`}>
      <InterruptFormContent
        interrupt={selectedItem.interrupt}
        task={selectedItem.task}
      />

      {interrupts.length > 1 && (
        <div className="flex items-center gap-1">
          <IconButton
            label="Previous interrupt"
            icon={ArrowLeftIcon}
            onClick={() => setSelectedIndex((prev) => prev - 1)}
            disabled={selectedIndex === 0}
            color="secondary"
            variant="plain"
            tooltipProps={{
              side: 'bottom',
            }}
            className="bg-transparent"
          />
          <IconButton
            label="Next interrupt"
            icon={ArrowRightIcon}
            onClick={() => setSelectedIndex((prev) => prev + 1)}
            disabled={selectedIndex === interrupts.length - 1}
            color="secondary"
            variant="plain"
            tooltipProps={{
              side: 'bottom',
            }}
            className="bg-transparent"
          />
          <Text variant="xs" weight="medium" className="text-secondary">
            {selectedIndex + 1} / {interrupts.length}
          </Text>
        </div>
      )}
    </div>
  );
};
